import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn, Store, ChefHat, Building2, ArrowLeft, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuthStore, useThemeStore } from "@/store/useStore";
import { markSessionStart } from "@/lib/idle-timeout";
import { preloadPOSData } from "@/lib/pos-preloader";
import { isDemoMode } from "@/lib/demoMode";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const demoMode = isDemoMode();
  const { setAuth, isAuthenticated, user } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const [propertyCode, setPropertyCode] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.is_super_admin) {
        navigate("/superadmin");
      } else {
        navigate("/admin");
      }
    }
  }, [isAuthenticated, navigate, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");

    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_URL}/auth/login`, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.onload = function () {
          let data = null;
          try { data = JSON.parse(xhr.responseText); } catch { data = null; }
          resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data });
        };
        xhr.onerror = function () { reject(new Error("network")); };
        xhr.send(JSON.stringify({ username, password, property_code: propertyCode || null }));
      });

      // Handle rate limiting
      if (result.status === 429) {
        setLoginError("Too many login attempts. Please wait a minute and try again.");
        return;
      }

      if (!result.ok) {
        const detail = typeof result.data?.detail === "string" ? result.data.detail : "";
        if (detail.toLowerCase().includes("property code")) {
          setLoginError("Invalid property code. Check the welcome email sent to your organization for the correct code.");
        } else if (detail.toLowerCase().includes("suspended")) {
          setLoginError("Your subscription is suspended. Please contact your administrator to renew.");
        } else if (detail.toLowerCase().includes("not active")) {
          setLoginError("Your organization's account is not active. Please contact support.");
        } else if (detail.toLowerCase().includes("disabled")) {
          setLoginError("Your user account has been disabled. Please contact your administrator.");
        } else {
          setLoginError(detail || "Incorrect username or password. Please check and try again.");
        }
        return;
      }

      const data = result.data;
      setAuth(data.user, data.access_token);
      markSessionStart();
      toast.success(`Welcome back, ${data.user.full_name}!`);
      
      // Preload POS data in background (non-blocking)
      if (!data.user.is_super_admin) {
        preloadPOSData(data.user);
      }
      
      if (data.user.is_super_admin) {
        navigate("/superadmin");
      } else {
        navigate("/admin");
      }
    } catch (error) {
      setLoginError("Unable to connect to the server. Please check your internet connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setPropertyCode("DIANI_PLATE");
    setUsername("demo_admin");
    setPassword("Demo@2026");
    setLoginError("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <ChefHat className="w-8 h-8 text-primary" />
          </Link>
          <h1 className="text-4xl font-bold tracking-tight text-foreground font-[Manrope]">
            HostPoint
          </h1>
          <p className="text-muted-foreground mt-2">Sign in to your account</p>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-[Manrope]">Welcome back</CardTitle>
            <CardDescription>
              Enter your credentials to access your dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Login Error Message */}
              {loginError && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="login-error-message">
                  {loginError}
                </div>
              )}
              {/* Property Code */}
              <div className="space-y-2">
                <Label htmlFor="propertyCode" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Property Code
                </Label>
                <Input
                  id="propertyCode"
                  data-testid="login-property-code"
                  placeholder="e.g., DIANI_PLATE"
                  value={propertyCode}
                  onChange={(e) => setPropertyCode(e.target.value.toUpperCase())}
                  autoComplete="organization"
                  className="uppercase"
                />
                <p className="text-xs text-muted-foreground flex items-start gap-1">
                  <Info className="h-3 w-3 mt-0.5 shrink-0" />
                  Your property code was sent in the welcome email. Leave empty for platform admin login.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  data-testid="login-username"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setLoginError(""); }}
                  required
                  autoComplete="username"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setLoginError(""); }}
                    required
                    autoComplete="current-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                <div className="flex justify-end">
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Sign in
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-border">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/pin-login")}
                data-testid="goto-pin-login"
              >
                <Store className="mr-2 h-4 w-4" />
                POS Login (PIN)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Builders Contest — demo judge access (only when opened with ?demo=1) */}
        {demoMode && (
        <Card className="mt-4 border-primary/40 bg-primary/5" data-testid="contest-demo-card">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 mb-3">
              <Store className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Contest Judge Demo Access</span>
            </div>
            <div className="grid grid-cols-1 gap-1.5 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Property Code</span>
                <span className="font-mono font-semibold text-foreground" data-testid="demo-property-code">DIANI_PLATE</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Username</span>
                <span className="font-mono font-semibold text-foreground" data-testid="demo-username">demo_admin</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Password</span>
                <span className="font-mono font-semibold text-foreground" data-testid="demo-password">Demo@2026</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">POS PIN</span>
                <span className="font-mono font-semibold text-foreground" data-testid="demo-pin">246810</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Admin access to the Restaurant &amp; Bar outlets. Use the PIN with the property code for POS / mobile login.
            </p>
            <Button
              type="button"
              variant="secondary"
              className="w-full mt-3"
              onClick={fillDemo}
              data-testid="use-demo-credentials"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Use demo credentials
            </Button>
          </CardContent>
        </Card>
        )}

        <div className="mt-6 text-center space-y-2">
          <Link to="/register" className="text-sm text-primary hover:underline">
            Don&apos;t have an account? Register here
          </Link>
          <div>
            <Button variant="ghost" size="sm" onClick={toggleTheme}>
              {theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
            </Button>
          </div>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
